/**
 * Phase 5.4 — personal WebSocket. One per logged-in user, lives across pages.
 *
 * Receives: personal_ready, ping, friend_request_received, friend_request_accepted,
 *           friend_request_declined, friend_status_changed, lobby_invite_received,
 *           lobby_invite_declined.
 *
 * Auto-reconnects with backoff. Emits inbound frames to any subscriber registered
 * via onEvent(). React components consume via the usePersonalEvents() helper.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const BACKOFF_MS = [500, 1000, 2000, 4000, 8000];

// Lightweight pub/sub so multiple components can react to the same WS frames.
const listeners = new Set();
export function onPersonalEvent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function fanout(evt) {
  for (const fn of listeners) {
    try { fn(evt); } catch { /* swallow */ }
  }
}

function _wsUrlBase() {
  const http = process.env.REACT_APP_BACKEND_URL || "";
  return http.replace(/^https/, "wss").replace(/^http/, "ws");
}

export function usePersonalSocket(token) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const attemptRef = useRef(0);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    closedByUsRef.current = false;

    function connect() {
      const url = `${_wsUrlBase()}/api/ws/personal?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (_) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (e) => {
        let frame;
        try { frame = JSON.parse(e.data); } catch { return; }
        if (frame.type === "ping") {
          try { ws.send(JSON.stringify({ type: "pong" })); } catch { /* */ }
          return;
        }
        // Surface toasts for high-signal events; all subscribers also get them.
        if (frame.type === "friend_request_received") {
          const u = frame.payload?.from_user || {};
          toast.message(`${u.username || "Someone"} sent you a friend request`, {
            duration: 6000,
            id: `frr-${frame.payload?.friendship_id}`,
          });
        } else if (frame.type === "friend_request_accepted") {
          const u = frame.payload?.friend || {};
          toast.success(`You are now friends with ${u.username || "your new friend"}`,
            { duration: 4000 });
        } else if (frame.type === "lobby_invite_received") {
          const u = frame.payload?.from_user || {};
          const s = frame.payload?.lobby_settings_snapshot || {};
          toast.message(
            `${u.username || "A friend"} invited you to a ${s.difficulty || ""} ${s.game_mode || ""} lobby`,
            {
              duration: 10000,
              id: `inv-${frame.payload?.invite_id}`,
              description: `Code ${frame.payload?.lobby_code} · ${s.rounds_count} rounds`,
            }
          );
        } else if (frame.type === "ranked_match_summary") {
          const p = frame.payload || {};
          const delta = p.mmr_delta || 0;
          const sign = delta >= 0 ? "+" : "";
          const tierChanged = p.tier_before !== p.tier_after;
          const tierMsg = tierChanged ? ` · ${p.tier_before} → ${p.tier_after}` : "";
          const fn = delta >= 0 ? toast.success : toast.warning;
          fn(`Ranked ${p.mode?.replace("_", " ")} · #${p.placement}/${p.total_players}`, {
            duration: 7000,
            id: `rms-${Date.now()}`,
            description: `${sign}${delta} MMR (${p.mmr_before} → ${p.mmr_after})${tierMsg}`,
          });
        } else if (frame.type === "ranked_match_found") {
          const pl = frame.payload?.players || [];
          toast.success("Ranked match found!", {
            duration: 6000,
            id: `rmf-${frame.payload?.lobby_code}`,
            description: `${pl.length} players · ${frame.payload?.difficulty} · starting in 5s`,
          });
        }
        fanout(frame);
      };
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (!closedByUsRef.current) scheduleReconnect();
      };
      ws.onerror = () => { /* close handler takes over */ };
    }

    function scheduleReconnect() {
      const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)];
      attemptRef.current += 1;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, delay);
    }

    connect();
    return () => {
      closedByUsRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(1000, "client unmount"); } catch { /* */ }
      wsRef.current = null;
    };
  }, [token]);

  return { connected };
}
